/**
 * Formats duration in minutes to a human-readable string.
 * Examples: 30 -> "30m", 60 -> "1h", 90 -> "1h 30m"
 */
export const formatDuration = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
};

/**
 * Calculates end time given a start time and duration.
 * @param startTime - Start time in "HHmm" format (e.g., "0900", "1430")
 * @param durationMinutes - Duration in minutes
 * @returns End time in "HHmm" format
 */
export const calculateEndTime = (startTime: string, durationMinutes: number): string => {
  const startHours = parseInt(startTime.substring(0, 2), 10);
  const startMins = parseInt(startTime.substring(2, 4), 10);

  const totalMinutes = startHours * 60 + startMins + durationMinutes;
  // Ending exactly at midnight is "2400" (end of THIS day) — "0000" reads as before the start.
  if (totalMinutes === 24 * 60) return "2400";
  const endHours = Math.floor(totalMinutes / 60) % 24; // Wrap around midnight
  const endMins = totalMinutes % 60;

  return `${endHours.toString().padStart(2, "0")}${endMins.toString().padStart(2, "0")}`;
};

/**
 * Formats time from "HHmm" to "HH:mm" display format.
 * @param time - Time in "HHmm" format (e.g., "0900", "1430")
 * @returns Time in "HH:mm" format (e.g., "09:00", "14:30")
 */
export const formatTimeForDisplay = (time: string): string => {
  if (!time || time.length !== 4) return time;
  return `${time.substring(0, 2)}:${time.substring(2, 4)}`;
};

/**
 * Formats a date for display.
 *
 * Rendered in UTC because a booking's calendar day is UTC-midnight of the centre's day. Formatting
 * in the browser's LOCAL timezone shows the previous day for viewers west of UTC (the travelling-
 * member bug) — the modal would read "Tue 25" while the calendar header the user clicked reads
 * "Wed 26". Keeping the timeZone at UTC matches formatUtcDayHeader and the rest of the calendar.
 *
 * @param date - Date object (UTC-midnight of the calendar day)
 * @returns Formatted date string (e.g., "Mon 24 Feb 2025")
 */
export const formatDateForDisplay = (date: Date): string => {
  return date.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
};

/**
 * Creates a slug from a location name.
 * @param name - Location name (e.g., "Bristol Clifton")
 * @returns Slug (e.g., "bristol-clifton")
 */
export const createLocationSlug = (name: string): string => {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
};
